import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Input
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

// Select and DND
import Select, { components } from 'react-select';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// Custom CSS
import "./InfoEditor.css";

// Common Components
import ThumbnailUploader from './components/ThumbnailUploader';
import StatusSelect from "./components/StatusSelect";
import StatBox from "./StatBox";
import HorizontalLoader from 'components/Common/HorizontalLoader';

// Custom Hooks and utils
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";


// Status Dropdown Control
const CustomControl = (props) => {
  return (
    <div onMouseDown={(e) => {
        e.stopPropagation(); // Prevent default so dropdown opens reliably
        e.preventDefault();
        props.selectProps.onMenuOpen();
    }}>
      <components.Control {...props} />
    </div>
  );
};


/*----------------------
/* Main Component
/*----------------------*/

const InfoEditor = ({ t }) => {

  const navigate = useNavigate();


  //--------------------------------------------------
  // States
  //--------------------------------------------------
  const [openSelectId, setOpenSelectId] = useState(null);
  const [infoStats, setInfoStats] = useState(null);

  const [infoThemes, setInfoThemes] = useState([]);
  const [infoLoading, setInfoLoading] = useState(true);
  const [error, setError] = useState(null);

  const [themeStats, setThemeStats] = useState({});

   const formatPercent = (part, total) => {
    if (!total || typeof part !== "number") return "0.0%";
    return `${((part / total) * 100).toFixed(1)}%`;
  };


  //--------------------------------------------------
  // Fetch on mount
  //--------------------------------------------------

  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [sortedThemes, setSortedThemes] = useState([]);

  const fetchThemes = async () => {
    try {
      setInfoLoading(true);
      setError(null);

      // Load theme and collections in parallel
      const [statsResponse, themesResponse] = await Promise.all([
        axiosInstance.get(`/knovia/info/stats`),
        axiosInstance.get(`/knovia/infoThemes`)
      ]);

      console.log('infoThemes Loaded:', themesResponse.data);
      console.log("Stats loaded:", statsResponse.data);

      setInfoThemes(themesResponse.data);

      // Sort by `order` before setting state
      const sorted = [...themesResponse.data].sort((a, b) => a.order - b.order);
      setSortedThemes(sorted);
      setInfoThemes(sorted);

      setInfoStats(statsResponse.data);

      // Set Initial Saved Status
      const initialSavedStatus = {};
      sorted.forEach(c => {
        initialSavedStatus[c._id] = true;
      });
      setIsSavedStatus(initialSavedStatus);

    } catch (err) {
      console.error(err);
      setError("Failed to load data.");
    } finally {
      setInfoLoading(false);
    }
  };

  const fetchCompletionStats = async (themes) => {
    try {
      const statsPromises = themes.map(async (theme) => {
        try {
          const res = await axiosInstance.get(`/knovia/infoTheme/${theme._id}/getCompletionStats`);
          return { id: theme._id, stats: res.data };
        } catch (err) {
          console.error(`Failed to fetch stats for ${theme._id}`, err);
          return { id: theme._id, stats: { total: 0, completed: 0 } }; // fallback
        }
      });
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(({ id, stats }) => {
        statsMap[id] = stats;
      });
      setThemeStats(statsMap);
    } catch (err) {
      console.error(err);
      setError("Failed to load theme completion stats.");
    }
  };

  const fetchThumbnails = async (themes) => {
    try {
      const thumbnailMap = await fetchThumbnailsWithCache(
        themes,
        "infoTheme",
        "sizeSmall",
        ({ id, url }) => {
          setThumbnailUrls(prev => ({
            ...prev,
            [id]: url
          }));
        },
      );
    } catch (err) {
      console.error("Thumbnail fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchThemes();
  }, []);

  useEffect(() => {
    if (sortedThemes.length > 0) {
      // Slight delay to allow UI render first
      setTimeout(() => {
        fetchCompletionStats(sortedThemes);
        fetchThumbnails(sortedThemes);
      }, 0);
    }
  }, [sortedThemes]);



  //--------------------------------------------------
  // ADD/DELETE Themes
  //--------------------------------------------------
  const handleAddTheme = async () => {
    try {

      const newThemePayload = {
        label: "New Theme"
      };

      const res = await axiosInstance.post("/knovia/infoTheme", newThemePayload);
      setInfoThemes((prev) => [...prev, res.data]);

    } catch (err) {
      console.error(err);
      setError("Failed to add theme.");
    }
  };

  const handleDeleteTheme = async (themeId) => {
    try {
      // Find the deleted theme's order before filtering
      const deletedTheme = infoThemes.find((th) => th._id === themeId);
      if (!deletedTheme) return;

      const deletedOrder = deletedTheme.order;

      // Send delete request to backend
      await axiosInstance.delete(`/knovia/infoTheme/${themeId}`);

      // Adjust frontend state
      setInfoThemes((prev) => {
        const filtered = prev.filter((th) => th._id !== themeId);
        // Decrement order for all themes with a higher order
        return filtered.map((th) =>
          th.order > deletedOrder ? { ...th, order: th.order - 1 } : th
        );
      });

    } catch (err) {
      console.error(err);
      setError("Failed to delete theme.");
    }
  };


  //--------------------------------------------------
  // Status
  //--------------------------------------------------

  const [isSavingStatus, setIsSavingStatus] = useState({});
  const [isSavedStatus, setIsSavedStatus] = useState({});

  const statusOptions = [
    { value: 'needs_edit', label: '需要編輯' },
    { value: 'in_progress', label: '編輯中' },
    { value: 'finalized', label: '已完成' },
  ];

  const handleStatusChange = async (themeId, newStatus) => {
    // Set saving to true for this specific theme
    setIsSavingStatus(prev => ({ ...prev, [themeId]: true }));
    setIsSavedStatus(prev => ({ ...prev, [themeId]: false }));

    try {
      const res = await axiosInstance.patch(`/knovia/infoTheme/${themeId}/updateStatus`, {
        status: newStatus
      });

      const updatedBy = {
        name: res.data.lastModifiedBy?.username || 'Unknown',
        email: res.data.lastModifiedBy?.email || 'Unknown'
      };

      // Update local infoCollections with the new status
      setInfoThemes(prev =>
        prev.map(theme => 
          theme._id === themeId 
            ? { 
                ...theme, 
                status: newStatus,
                lastStatusUpdate: {
                  updatedAt: new Date(res.data.lastUpdatedAt),
                  updatedBy
                }
              } 
            : theme
        )
      );

      // Mark as saved
      setIsSavedStatus(prev => ({ ...prev, [themeId]: true }));

    } catch (err) {
      console.error('Failed to update status:', err);
      setError("Failed to update collection status.");
    } finally {
      // Clear saving status
      setIsSavingStatus(prev => ({ ...prev, [themeId]: false }));
    }
  };


  //--------------------------------------------------
  // Theme List DND
  //--------------------------------------------------

  const [isSavingThemeOrder, setIsSavingThemeOrder] = useState(false);
  const [isSavedThemeOrder, setIsSavedThemeOrder] = useState(true);
  const [isSavedThemeOrderError, setIsSavedThemeOrderError] = useState(false);

  // Fires when dropping dragged items
  const handleDragEnd = (result) => {

    setIsSavedThemeOrderError(false);
    setIsSavedThemeOrder(false);

    // If there's no valid destination (e.g. dropped outside), do nothing
    if (!result.destination) return;

    // Reorder the array in state
    const newOrder = Array.from(infoThemes);
    // extract the moved item
    const [movedItem] = newOrder.splice(result.source.index, 1);
    // add movedItem to result.destination.index
    newOrder.splice(result.destination.index, 0, movedItem);

    setInfoThemes(newOrder);

    handleSaveThemeOrder(newOrder);
  };

  // Saving new order
  const handleSaveThemeOrder = async (newOrder) => {
    try {

      const payload = newOrder.map((th, index) => ({
        _id: th._id,
        label: th.label,
        order: index + 1,
      }));
      console.log('Saving theme order:', payload);
      setIsSavingThemeOrder(true);

      await axiosInstance.patch(
        `/knovia/info/reorderThemes`,
        payload
      );

      // Update local state after successful save
      const reorderedThemes = payload.map(({ _id, order }) => {
        const matchingTheme = infoThemes.find(th => th._id === _id);
        return { ...matchingTheme, order };
      });

      setInfoThemes(reorderedThemes.sort((a, b) => a.order - b.order));

      //alert("Theme order saved!");
      setIsSavingThemeOrder(false);
      setIsSavedThemeOrder(true);

    } catch (err) {
      console.error(err);
      setIsSavingThemeOrder(false);
      setIsSavedThemeOrderError(true);
      setError("Failed to save theme order.");
    }
  };

  const sendSMSTest = async()=>{
    try {
      console.log('Sending SMS Test');
      const ascii6 = String.fromCharCode(6);

      const res = await axiosInstance.post("https://backend.thechart.ai/knovia/myInfo/sendSMS", 
      //const res = await axiosInstance.post("/knovia/myInfo/sendSMS", 
        {
          CharsetURL: 'UTF-8',
          //CharsetURL: 'Big5',
          dstaddr: '0953579933',
          smbody: `簡訊🦒測試🦥UTF8🐫${ascii6}✔️請完成號碼驗證`,
        }
      );

      console.log('res.data:', res.data);
    } catch (e){
      console.log('Failed to send SMS Test:', e);
    }
  }

  //--------------------------------------------------
  // Render Logic (Not in use)
  //--------------------------------------------------
  /*
  if (infoLoading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  */


  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>

              {/* Title and Button row */}
              <Row>
                <Col xs={6}>
                  <CardTitle className="mb-4">{t("編輯類別總表")}</CardTitle>
                </Col>
                <Col xs={6} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                </Col>
              </Row>

              <Button color="outline-primary"
                onClick={sendSMSTest}
              >測試簡訊</Button>

              {/* Progress Stats */}
              <Row style={{ padding: "1rem" }}>
                <Col xs={12}>

                  {(() => {
                    const formatPercent = (part, total) => {
                      if (!total || typeof part !== "number") return "0.0%";
                      return `${((part / total) * 100).toFixed(1)}%`;
                    };

                    return (
                      <Row style={{ marginBottom: "0.5rem" }}>
                        <Col xl={1}></Col>

                        <Col xs={6} sm={4} md={3} lg={3} xl={2} style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "1rem" }}>
                          <StatBox label="Theme" value={infoStats?.themeCount || 0} loading={infoLoading} />
                          <p>{`+${infoStats?.newThemes24h || 0} (+${formatPercent(infoStats?.newThemes24h, infoStats?.themeCount)})`}</p>
                        </Col>

                        <Col xs={6} sm={4} md={3} lg={3} xl={2} style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "1rem" }}>
                          <StatBox label="Collection" value={infoStats?.collectionCount || 0} loading={infoLoading} />
                          <p>{`+${infoStats?.newCollections24h || 0} (+${formatPercent(infoStats?.newCollections24h, infoStats?.collectionCount)})`}</p>
                        </Col>

                        <Col xs={6} sm={4} md={3} lg={3} xl={2} style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "1rem" }}>
                          <StatBox label="Topic" value={infoStats?.topicCount || 0} loading={infoLoading} 
                            numberStyles={{ fontSize: "2.8rem", marginTop: "0.1rem" }}
                          />
                          <p>{`+${infoStats?.newTopics24h || 0} (+${formatPercent(infoStats?.newTopics24h, infoStats?.topicCount)})`}</p>
                        </Col>

                        <Col xs={6} sm={4} md={3} lg={3} xl={2} style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "1rem" }}>
                          <StatBox label="QuestionSet" value={infoStats?.questionSetCount || 0} loading={infoLoading} />
                          <p>{`+${infoStats?.newQuestionSets24h || 0} (+${formatPercent(infoStats?.newQuestionSets24h, infoStats?.questionSetCount)})`}</p>
                        </Col>

                        <Col xs={6} sm={4} md={3} lg={3} xl={2} style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "1rem" }}>
                          <StatBox label="Question" value={infoStats?.questionCount || 0} loading={infoLoading} />
                          <p>{`+${infoStats?.newQuestions24h || 0} (+${formatPercent(infoStats?.newQuestions24h, infoStats?.questionCount)})`}</p>
                        </Col>

                        <Col xl={1}></Col>
                      </Row>
                    );
                  })()}



                  <hr />


                  {/* Theme Edit */}

                  <Row style={{ marginBottom: "0.5rem" }}>
                    <Col xs={12} style={{ display: "flex", justifyContent: "space-between", padding: "0" }}>

                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col xs={12} md={4}>
                          <h3>所有類別</h3>
                          {isSavingThemeOrder && <span style={{ color: "#f1b34c" }}>Saving Order...</span>}
                          {!isSavingThemeOrder && !isSavedThemeOrderError &&
                            <span style={{ color: isSavedThemeOrder ? "#34c38f" : "#aeabab" }}>✔ Saved Order</span>
                          }
                          {!isSavingThemeOrder && isSavedThemeOrderError &&
                            <span style={{ color: "red" }}>Error Saving Theme Order!</span>
                          }
                        </Col>
                        <Col xs={12} md={8} style={{ display:"flex", alignItems:"center", justifyContent:"flex-end" }}>
                          <Button color="outline-info" onClick={handleAddTheme} style={{ marginRight: "1rem" }}>
                            + 新增類別
                          </Button>
                        </Col>
                      </Row>

                    </Col>
                  </Row>

                  {/* Theme Drag List */}
                  <Row>
                    <Col xs={12} style={{ padding: "0.5rem 3rem", border: "1px solid #bcbbbb", borderRadius: "5px", minHeight: "300px" }}>
                      {infoThemes.length === 0 ? (
                        <HorizontalLoader />
                      ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="collectionList">
                            {(provided) => (
                              <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                              >
                                {infoThemes.map((th, index) => (

                                  <Draggable key={th._id} draggableId={th._id} index={index}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        {/* Single row content */}
                                        <Row key={th._id} className="mt-3 mb-4 mb-mt-0 mb-md-0" style={{ minHeight: "5.5rem" }}>

                                          <Col xs={12} md={8} style={{ display: "flex", alignItems: "center", padding: "0.25rem 0rem" }}>
                                            
                                            <div style={{ minWidth: "75px", display: "flex", padding: "0px 15px 0px 0px" }}>
                                              <div
                                                style={{
                                                  height: '60px',
                                                  width: '60px',
                                                  borderRadius: '50%',
                                                  backgroundImage: `url(${thumbnailUrls[th._id] || placeholderSmall})`,
                                                  backgroundSize: 'cover',
                                                  backgroundPosition: 'center',
                                                  transition: 'background-image 0.5s ease-in-out',
                                                }}
                                              />
                                            </div>

                                            <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                              <Col xs={12} style={{ padding: 0 }}>
                                                <div style={{ position: "relative", width: "100%", height: "100%", minHeight: "3rem", display: "flex", alignItems: "center" }}>
                                                  {/* Background Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    backgroundColor: "#f0f0f0",  // base gray
                                                    borderRadius: "5px",
                                                    zIndex: 0,
                                                  }} />

                                                  {/* Progress Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: `${themeStats[th._id] ? (themeStats[th._id].completed / themeStats[th._id].total) * 100 : 0}%`,
                                                    height: "100%",
                                                    backgroundColor: "#d3eaff", // blue highlight
                                                    borderRadius: "5px 0 0 5px",
                                                    zIndex: 1,
                                                    transition: "width 1.2s ease-in-out"
                                                  }} />

                                                  {/* Text content */}
                                                  <div style={{
                                                    position: "relative",
                                                    zIndex: 2,
                                                    padding: "0.5rem 1rem",
                                                    width: "100%",
                                                  }}>
                                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                                      {`${th.order}. ${th.label}`}{" "}
                                                      {themeStats[th._id] && (
                                                        <span style={{ fontSize: "0.9rem", fontWeight: 400 }}>
                                                          （{themeStats[th._id].completed}/{themeStats[th._id].total} 主題集已完成編輯）
                                                        </span>
                                                      )}
                                                    </p>
                                                    {th.lastStatusUpdate && (
                                                      <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.2rem", lineBreak: 'anywhere' }}>
                                                        最近狀態由 <strong>{th.lastStatusUpdate.updatedBy.name}</strong>（{th.lastStatusUpdate.updatedBy.email}）<br />
                                                        於 {new Date(th.lastStatusUpdate.updatedAt).toLocaleString()} 更新
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </Col>
                                            </Row>
                                          </Col>


                                          {/* Edit and Delete Themes */}
                                          <Col xs={4} sm={6} md={2} 
                                            className="d-flex align-items-center justify-content-start justify-content-sm-center"
                                          >
                                            <Button color="outline-info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                                              onClick={(e) => { navigate(`/editInfoThemes/${th._id}`); }}
                                            >
                                              <i className="bx bxs-pencil" />
                                            </Button>

                                            <Button color="outline-danger" onClick={() => handleDeleteTheme(th._id)}>
                                              -
                                            </Button>
                                          </Col>

                                          {/* Theme Edit Status */}
                                          <Col xs={8} sm={6} md={2}
                                            className="d-flex flex-column justify-content-center align-items-center"
                                          >
                                            <div className="w-100 px-1 px-md-0">
                                              <StatusSelect
                                                value={th.status}
                                                onChange={(newStatus) => handleStatusChange(th._id, newStatus)}
                                                options={statusOptions}
                                                selectId={th._id}
                                                openSelectId={openSelectId}
                                                setOpenSelectId={setOpenSelectId}
                                              />
                                            </div>

                                            <div className="w-100 px-1 px-md-0">
                                              {isSavingStatus[th._id] && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                                              {!isSavingStatus[th._id] && (
                                                <span style={{ color: isSavedStatus[th._id] ? "#34c38f" : "#aeabab" }}>
                                                  ✔ Saved!
                                                </span>
                                              )}
                                            </div>

                                          </Col>
                                        </Row>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}

                    </Col>
                  </Row>


                </Col>
              </Row>


            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(InfoEditor);


